import { useCreatePayUp, useDeletePayUp } from '@hooks'
import { useCreateExpense } from '@hooks/useCreateExpense'
import { useDeleteExpense } from '@hooks/useDeleteExpense'
import { useDeleteExpensePicture } from '@hooks/useDeleteExpensePicture'
import { useUpdateExpense } from '@hooks/useUpdateExpense'
import { useUploadExpensePicture } from '@hooks/useUploadExpensePicture'
import { GetExpenseResponse, GetPayUpType, GetUserType, PostPayUpType } from '@orbit/api-types'
import toast from 'react-hot-toast'
import {
	AddExpenseSubmission,
	EditExpenseSubmission,
	expenseImageIntent,
} from './expenseForm'

interface Options {
	tripId: string | undefined
	me: GetUserType | undefined
	editingExpense: GetExpenseResponse | null
	deletingExpense: GetExpenseResponse | null
	deletingPayUp: GetPayUpType | null
	onCloseAddExpense: () => void
	onCloseEditExpense: () => void
	onCloseDeleteExpense: () => void
	onCloseAddPayUp: () => void
	onCloseDeletePayUp: () => void
	onCloseViewingPayUp: () => void
}

export const useBalancesMutations = (opts: Options) => {
	const createExpense = useCreateExpense()
	const updateExpense = useUpdateExpense()
	const deleteExpense = useDeleteExpense()
	const uploadExpensePicture = useUploadExpensePicture()
	const deleteExpensePicture = useDeleteExpensePicture()

	const createPayUp = useCreatePayUp()
	const deletePayUp = useDeletePayUp()

	const handleAddExpense = ({ payload, imageFile }: AddExpenseSubmission) => {
		const { tripId, onCloseAddExpense } = opts
		if (!tripId) return
		createExpense.mutate(
			{ tripId, expenseData: payload },
			{
				onSuccess: created => {
					if (imageFile) {
						uploadExpensePicture.mutate(
							{ tripId, expenseId: created.id, file: imageFile },
							{
								onSuccess: () => {
									toast.success('Expense added!')
									onCloseAddExpense()
								},
								onError: () => {
									toast.success('Expense added, but image upload failed.')
									onCloseAddExpense()
								},
							},
						)
					} else {
						toast.success('Expense added!')
						onCloseAddExpense()
					}
				},
				onError: () => toast.error('Failed to add expense. Please try again.'),
			},
		)
	}

	const handleEditExpense = ({ payload, image }: EditExpenseSubmission) => {
		const { tripId, editingExpense, onCloseEditExpense } = opts
		if (!tripId || !editingExpense) return

		// The expense itself saved either way, so a failed image step still closes
		// the modal on a success toast — it just says what did not happen.
		const finish = (message = 'Expense updated!') => {
			toast.success(message)
			onCloseEditExpense()
		}

		const handleImageAfterUpdate = () => {
			const intent = expenseImageIntent(image)
			if (intent.kind === 'remove') {
				deleteExpensePicture.mutate(
					{ tripId, expenseId: editingExpense.id, fileId: intent.fileId },
					{
						onSuccess: () => finish(),
						onError: () => finish('Expense updated, but image removal failed.'),
					},
				)
			} else if (intent.kind === 'upload') {
				uploadExpensePicture.mutate(
					{ tripId, expenseId: editingExpense.id, file: intent.file },
					{
						onSuccess: () => finish(),
						onError: () => finish('Expense updated, but image upload failed.'),
					},
				)
			} else {
				finish()
			}
		}

		updateExpense.mutate(
			{ tripId, expenseId: editingExpense.id, expenseData: payload },
			{
				onSuccess: handleImageAfterUpdate,
				onError: () => toast.error('Failed to update expense. Please try again.'),
			},
		)
	}

	const handleDeleteExpense = () => {
		const { tripId, deletingExpense, onCloseDeleteExpense, onCloseEditExpense } = opts
		if (!tripId || !deletingExpense) return
		deleteExpense.mutate(
			{ tripId, expenseId: deletingExpense.id },
			{
				onSuccess: () => {
					toast.success('Expense deleted')
					onCloseDeleteExpense()
					onCloseEditExpense()
				},
				onError: () => toast.error('Failed to delete expense. Please try again.'),
			},
		)
	}

	const handleAddPayUp = (payload: PostPayUpType) => {
		const { tripId, me, onCloseAddPayUp } = opts
		if (!tripId || !me) return
		createPayUp.mutate(
			{ tripId, currentUserId: me.id, payUpData: payload },
			{
				onSuccess: () => {
					toast.success('Payup recorded!')
					onCloseAddPayUp()
				},
				onError: () => toast.error('Failed to record payup. Please try again.'),
			},
		)
	}

	const handleDeletePayUp = () => {
		const { tripId, deletingPayUp, onCloseDeletePayUp, onCloseViewingPayUp } = opts
		if (!tripId || !deletingPayUp) return
		deletePayUp.mutate(
			{ tripId, payUpId: deletingPayUp.id },
			{
				onSuccess: () => {
					toast.success('Payup deleted')
					onCloseDeletePayUp()
					onCloseViewingPayUp()
				},
				onError: () => toast.error('Failed to delete payup. Please try again.'),
			},
		)
	}

	return {
		handleAddExpense,
		handleEditExpense,
		handleDeleteExpense,
		handleAddPayUp,
		handleDeletePayUp,
		isExpenseSubmitting:
			createExpense.isPending || uploadExpensePicture.isPending || deleteExpensePicture.isPending,
		isEditExpenseSubmitting:
			updateExpense.isPending || uploadExpensePicture.isPending || deleteExpensePicture.isPending,
		isPayUpSubmitting: createPayUp.isPending,
		isDeletingPayUpPending: deletePayUp.isPending,
	}
}
